import { Menu } from '@mui/icons-material';
import {
  OrganizationType,
  type PositionFilter,
  PositionType,
  rdbV1FiltersPositionsList,
  RecruitmentStatus,
  type SearchV1PeopleListParams,
  type SlimCoachResponse,
  type SlimOrganizationRes,
} from '@on3/api';
import { On3IconArrowDown } from '@on3/icons';
import { type OptionType } from '@on3/ui-lib/components/AutoComplete/AutoComplete';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { PersonAutoComplete } from '@on3/ui-lib/components/AutoComplete/PersonAutoComplete';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Pagination } from '@on3/ui-lib/components/Pagination/Pagination';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { CloseButton } from '@on3/ui-lib/components/Svg/CloseButton';
import { externalApi, useSite } from '@on3/ui-lib/index';
import { formatFilterOptions } from '@on3/ui-lib/utils/formatters';
import { useRouter } from 'next/router';
import type { IPlayerSearchPage } from 'pages/rivals/search';
import React, { useCallback, useEffect, useReducer, useState } from 'react';

import styles from './AdvancedSearch.module.scss';
import { AdvancedSearchItem } from './AdvancedSearchItem';
import { LocationFilter } from './LocationFilter';
import { OrganizationLevelFilter } from './OrganizationLevelFilter';

export enum SearchKeys {
  ARTICLE_TAGGING_ENABLED = 'articleTaggingEnabled',
  ATHLETE_VERIFIED = 'athleteVerified',
  ATTENDED_OR_GRADUATED_FROM_ORG_KEY = 'attendedOrGraduatedFromOrgKey',
  COLLEGE_ORG_KEYS = 'collegeOrgKeys',
  COMMITTED_ORGANIZATION_KEY = 'committedOrganizationKey',
  CURRENT_ORGANIZATION_KEY = 'currentOrganizationKey',
  CURRENT_ORGANIZATION_LEVEL = 'currentOrganizationLevel',
  DISTANCE_FROM_LATITUDE = 'distanceFromLatitude',
  DISTANCE_FROM_LONGITUDE = 'distanceFromLongitude',
  DISTANCE_FROM_ORG_KEY = 'distanceFromOrgKey',
  DISTANCE_FROM_ZIPCODE = 'distanceFromZipCode',
  DISTANCE_FROM_MILES = 'distanceFromMiles',
  FIRST_NAME = 'firstName',
  GOES_BY = 'goesBy',
  HIGH_SCHOOL_ORG_KEYS = 'highSchoolOrgKeys',
  HOME_STATE_KEY = 'homeStateKey',
  IS_DRAFT_PICK = 'isDraftPick',
  IS_FIVE_STAR_PLUS = 'isFiveStarPlus',
  LAST_NAME = 'lastName',
  LIMIT = 'limit',
  MAX_CLASS_YEAR = 'maxClassYear',
  MAX_CONSENSUS_NATIONAL_RANK = 'maxConsensusNationalRank',
  MAX_NIL_VALUATION = 'maxNilValuation',
  MAX_OVERALL_NATIONAL_RANK = 'maxOverallNationalRank',
  MAX_STARS = 'maxStars',
  MIN_CLASS_YEAR = 'minClassYear',
  MIN_HEIGHT = 'minHeight',
  MIN_NIL_VALUATION = 'minNilValuation',
  MIN_STARS = 'minStars',
  MIN_WEIGHT = 'minWeight',
  OFFICIAL_VISIT_END_DATE = 'officialVisitEndDate',
  OFFICIAL_VISIT_ORG_KEYS = 'officialVisitOrgKeys',
  OFFICIAL_VISIT_START_DATE = 'officialVisitStartDate',
  PAGE = 'page',
  POSITION_KEY = 'positionKey',
  RECRUITED_BY_PERSON_KEY = 'recruitedByPersonKey',
  RECRUITING_STATUSES = 'recruitingStatuses',
  RECRUITMENT_INTEREST_ORG_KEYS = 'recruitmentInterestOrgKeys',
  SEARCH_TEXT = 'searchText',
  SPORT_KEY = 'sportKey',
  YEAR = 'year',
}

const playerStarOptions = [
  { label: 'Any', value: '' },
  { label: '3-Stars', value: '3' },
  { label: '4-Stars', value: '4' },
  { label: '5-Stars', value: '5' },
];

const recruitmentStatusOptions = Object.values(RecruitmentStatus)
  .filter(
    (status) =>
      status !== RecruitmentStatus.None &&
      status !== RecruitmentStatus.Decommitted,
  )
  .map((status) => ({
    label: status,
    value: status,
  }));

export type ActionType =
  | { type: 'search' }
  | { type: 'searched' }
  | {
      type: 'update';
      field: string;
      value: any;
    }
  | { type: 'reset' }
  | { type: 'count'; value: number };

interface State {
  searching: boolean;
  hasSearched: boolean;
  values: SearchV1PeopleListParams & {
    distanceFromOrgKey?: number;
    distanceFromZipCode?: number;
  };
  filterCount: number;
}
const initialState: State = {
  searching: false,
  hasSearched: false,
  values: {
    officialVisitEndDate: 0,
    officialVisitStartDate: 0,
  },
  filterCount: 0,
};
const reducer = (state: State, action: ActionType) => {
  switch (action.type) {
    case 'search':
      return { ...state, searching: true };
    case 'searched':
      return { ...state, searching: false, hasSearched: true };
    case 'update':
      return {
        ...state,
        values: { ...state.values, [action.field]: action.value },
      };
    case 'reset':
      return {
        ...state,
        values: { ...initialState.values },
      };

    case 'count':
      return { ...state, filterCount: action.value };
    default:
      return state;
  }
};

export const autoCompleteData = (
  value?:
    | SlimOrganizationRes
    | SlimCoachResponse
    | (SlimOrganizationRes | SlimCoachResponse)[],
): OptionType[] | undefined => {
  if (Array.isArray(value)) {
    const options = value
      .filter((item) => item?.key && item.fullName)
      .map((item) => ({
        key: item.key,
        label: item.fullName ?? 'Unknown',
        value: item.key,
      }));

    return options.length > 0 ? options : undefined;
  } else if (value?.key && value.fullName) {
    return [
      {
        key: value.key,
        label: value.fullName ?? 'Unknown',
        value: value.key,
      },
    ];
  }

  return undefined;
};

export const AdvancedSearch = ({
  searchData,
  yearsData,
  statesData,
  relatedModel,
}: IPlayerSearchPage) => {
  const { sportsData } = useSite();
  const { pagination } = searchData || {};
  const {
    highSchoolData,
    committedOrganizationData,
    recruitedByPersonData,
    officialVisitData,
    attendedGraduatedData,
    recruitmentInterestData,
    distanceFromOrgData,
  } = relatedModel || {};

  const router = useRouter();

  const { homeStateKey } = router.query;
  const initial: State = {
    ...initialState,
    values: {
      ...router.query,
      homeStateKey: homeStateKey ? +homeStateKey : undefined,
    },
  };

  const [state, dispatch] = useReducer(reducer, initial);

  const [positions, setPositions] = useState<PositionFilter[]>();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [allCollapsed, setAllCollapsed] = useState<boolean>(false);
  const [searchCollapse, setSearchCollapse] = useState(false);
  const [attendedOrGraduatedFrom, setAttendedOrGraduatedFrom] =
    useState<boolean>(!!state.values.attendedOrGraduatedFromOrgKey);

  const [interestedIn, setInterestedIn] = useState<boolean>(
    !!state.values.recruitmentInterestOrgKeys?.length,
  );

  const handleResets = () => {
    setAttendedOrGraduatedFrom(false);
    setInterestedIn(false);

    dispatch({ type: 'reset' });

    router.replace(
      {
        pathname: router.pathname,
        query: {},
      },
      undefined,
      { shallow: true },
    );
  };
  const fetchPositions = useCallback(async () => {
    try {
      const response = await rdbV1FiltersPositionsList(externalApi, {
        sportKey: state.values.sportKey,
        positionType: PositionType.Player,
      });

      setPositions(response);
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  }, [state.values.sportKey]);

  const handleFieldUpdate = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    const target = e.currentTarget as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLButtonElement;

    const { name, value } = target;

    dispatch({
      type: 'update',
      field: name,
      value,
    });
  };

  const sportsList = formatFilterOptions({
    data: sportsData,
    valueKey: 'key',
    labelKey: 'name',
    addAll: true,
  });

  const positionList = formatFilterOptions({
    data: positions,
    labelKey: 'name',
    valueKey: 'key',
    addAll: true,
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      dispatch({ type: 'search' });

      const { page, ...query } = router.query;

      const params = Object.entries(state.values).reduce(
        (acc, [key, value]) => {
          value ? (acc[key] = value) : delete acc[key];

          return acc;
        },
        { ...query },
      );

      router
        .push({
          pathname: '/rivals/search',
          query: params,
        })
        .then(() => {
          dispatch({ type: 'searched' });
          setSearchCollapse(true);
        });
    },
    [router, state.values],
  );
  const handleAutoCompleteChange = (value: OptionType[], field?: string) => {
    if (!field) return;

    dispatch({
      type: 'update',
      field,
      value: value.map((v) => v.key),
    });
  };

  const handleRecruitStatusChange = (
    statusValue: string,
    isChecked: boolean,
  ) => {
    const status = Array.isArray(state.values.recruitingStatuses)
      ? state.values.recruitingStatuses
      : state.values.recruitingStatuses
        ? [state.values.recruitingStatuses]
        : [];

    const newStatus = isChecked
      ? [...status, statusValue]
      : status.filter((s) => s !== statusValue);

    const validStatuses = Object.values(RecruitmentStatus);
    const filteredStatus = newStatus.filter((s) =>
      validStatuses.includes(s as RecruitmentStatus),
    );

    dispatch({
      type: 'update',
      field: SearchKeys.RECRUITING_STATUSES,
      value: filteredStatus,
    });

    if (newStatus.length === 0) {
      dispatch({
        type: 'update',
        field: SearchKeys.COMMITTED_ORGANIZATION_KEY,
        value: undefined,
      });
    }
  };

  const handleAttendedOrGraduatedFrom = useCallback((value: boolean) => {
    setAttendedOrGraduatedFrom(value);
    if (value === false) {
      dispatch({
        type: 'update',
        field: SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY,
        value: undefined,
      });
    }
  }, []);
  const handleInterestedIn = useCallback((value: boolean) => {
    setInterestedIn(value);
    if (value === false) {
      dispatch({
        type: 'update',
        field: SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS,
        value: undefined,
      });
    }
  }, []);

  useEffect(() => {
    if (!state.values.sportKey) return;
    fetchPositions();
  }, [fetchPositions, state.values.sportKey]);
  useEffect(() => {
    const {
      values: { searchText, limit, page, ...filters },
    } = state;

    const filteredFilters = Object.entries(filters).filter(
      ([key, value]) =>
        value !== '' &&
        value !== false &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0),
    );

    const filterCount = filteredFilters.length;

    if (state.filterCount !== filterCount) {
      dispatch({ type: 'count', value: filterCount });
    }
  }, [state]);

  return (
    <section className={styles.block}>
      <header className={styles.mainHeader}>
        <h1>Search</h1>
        <button name="reset" onClick={handleResets} type="button">
          Reset all
        </button>
      </header>
      <OrganizationLevelFilter
        currentOrganizationLevel={state.values.currentOrganizationLevel}
        handleFieldUpdate={handleFieldUpdate}
      />
      <form onSubmit={handleSubmit}>
        <Input
          id="search"
          label="Search by Name"
          name={SearchKeys.SEARCH_TEXT}
          onChange={handleFieldUpdate}
          placeholder="Enter Player or Coach name"
          suffix={
            state.values.searchText && (
              <button
                className={styles.clear}
                disabled={state.searching}
                name={SearchKeys.SEARCH_TEXT}
                onClick={handleFieldUpdate}
                type="button"
              >
                <span className="sr-only">Clear</span>
                <CloseButton />
              </button>
            )
          }
          value={state.values.searchText}
        />
        <button disabled={state.searching} name="search" type="submit">
          Search
        </button>
      </form>

      <aside className={styles.filters}>
        <header className={styles.filtersHeader}>
          <span className={styles.heading}>
            Filter Players
            {state.filterCount > 0 && ` (${state.filterCount})`}
          </span>
          <button
            name="collapse-all"
            onClick={() => {
              const newCollapseState = !allCollapsed;

              setSearchCollapse(newCollapseState);
              setAllCollapsed(newCollapseState);
            }}
            type="button"
          >
            <span className="sr-only">Collapse</span>
            <Menu />
          </button>
        </header>
        <div
          className={styles.allOptions}
          data-search-collapse={searchCollapse ? 'closed' : 'open'}
          data-status={allCollapsed ? 'closed' : 'open'}
        >
          <div className={styles.container}>
            <Select
              label="Sport"
              name={SearchKeys.SPORT_KEY}
              onChange={handleFieldUpdate}
              options={sportsList}
              value={state.values.sportKey?.toString()}
            />
            <div data-enabled={!!positionList.length}>
              <Select
                disabled={!positionList.length}
                label="Position"
                name={SearchKeys.POSITION_KEY}
                onChange={handleFieldUpdate}
                options={positionList}
                value={state.values.positionKey?.toString()}
              />
            </div>
          </div>
          <div
            className={styles.filterOptions}
            data-status={collapsed ? 'closed' : 'open'}
          >
            <div className={styles.container}>
              <div className={styles.vitals}>
                <Input
                  label="Min height"
                  name={SearchKeys.MIN_HEIGHT}
                  onChange={handleFieldUpdate}
                  type="number"
                  value={state.values.minHeight || ''}
                />
                <Input
                  label="Min weight"
                  name={SearchKeys.MIN_WEIGHT}
                  onChange={handleFieldUpdate}
                  type="number"
                  value={state.values.minWeight || ''}
                />
              </div>
              <Select
                id={SearchKeys.HOME_STATE_KEY}
                label="Home State"
                name={SearchKeys.HOME_STATE_KEY}
                onChange={handleFieldUpdate}
                options={[
                  { label: 'All', value: '' },
                  ...statesData.map((st) => ({
                    label: String(st.name),
                    value: String(st.key),
                  })),
                ]}
                value={String(state.values.homeStateKey)}
              />
              <OrganizationAutoComplete
                id={SearchKeys.HIGH_SCHOOL_ORG_KEYS}
                label="High School"
                name={SearchKeys.HIGH_SCHOOL_ORG_KEYS}
                onChange={handleAutoCompleteChange}
                type={OrganizationType.HighSchool}
                value={(autoCompleteData(highSchoolData) ?? []).filter(
                  (o) => o.key,
                )}
              />
            </div>
            <LocationFilter
              className={styles.container}
              dispatch={dispatch}
              distanceFromMiles={state.values.distanceFromMiles}
              distanceFromOrgData={distanceFromOrgData}
              distanceFromOrgKey={state.values.distanceFromOrgKey}
              distanceFromZipCode={state.values.distanceFromZipCode}
            />
            <div className={styles.container} data-name="recruiting">
              <h3>Recruiting</h3>
              <div className={styles.columns}>
                <Select
                  label="Class"
                  name={SearchKeys.MIN_CLASS_YEAR}
                  onChange={handleFieldUpdate}
                  options={[
                    { label: 'Any', value: '' },
                    ...yearsData.map((y) => ({
                      label: String(y),
                      value: String(y),
                    })),
                  ]}
                  value={state.values.minClassYear?.toString()}
                />
                <span className={styles.label}>To</span>
                <Select
                  label="Class"
                  name={SearchKeys.MAX_CLASS_YEAR}
                  onChange={handleFieldUpdate}
                  options={[
                    { label: 'Any', value: '' },
                    ...yearsData.map((y) => ({
                      label: String(y),
                      value: String(y),
                    })),
                  ]}
                  srLabel
                  value={state.values.maxClassYear?.toString()}
                />
              </div>
              <div className={styles.columns}>
                <Select
                  label="Player Stars"
                  name={SearchKeys.MIN_STARS}
                  onChange={handleFieldUpdate}
                  options={playerStarOptions}
                  value={state.values.minStars?.toString()}
                />
                <span className={styles.label}>To</span>
                <Select
                  label="Player Stars"
                  name={SearchKeys.MAX_STARS}
                  onChange={handleFieldUpdate}
                  options={playerStarOptions}
                  srLabel
                  value={state.values.maxStars?.toString()}
                />
              </div>
              <div className={styles.groupingSmall}>
                <span className={styles.label}>rankings lists</span>
                <span className={styles.grayLabel}>
                  Only include players that appear on:
                </span>
                <Checkbox
                  id={SearchKeys.IS_FIVE_STAR_PLUS}
                  key={`${SearchKeys.IS_FIVE_STAR_PLUS}-${state.values.isFiveStarPlus}`}
                  label="5-Star Plus"
                  onChange={(value) =>
                    dispatch({
                      value: String(value),
                      type: 'update',
                      field: SearchKeys.IS_FIVE_STAR_PLUS,
                    })
                  }
                  value={state.values.isFiveStarPlus ?? false}
                />
              </div>
            </div>
            <div className={styles.container}>
              <h3>Status</h3>
              <div className={styles.statusCheckboxes}>
                <span className={styles.grayLabel}>
                  Only include players with status:
                </span>
                <div>
                  {recruitmentStatusOptions.map((status, index) => (
                    <Checkbox
                      disabled={interestedIn || attendedOrGraduatedFrom}
                      id={status.label}
                      key={`${state.values.recruitingStatuses?.length}-${index}`}
                      label={status.label}
                      onChange={(isChecked) =>
                        handleRecruitStatusChange(status.value, isChecked)
                      }
                      value={Boolean(
                        state.values.recruitingStatuses?.includes(status.value),
                      )}
                    />
                  ))}
                  <div data-show={!!state.values.recruitingStatuses?.length}>
                    <OrganizationAutoComplete
                      id={SearchKeys.COMMITTED_ORGANIZATION_KEY}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.COMMITTED_ORGANIZATION_KEY}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={(
                        autoCompleteData(committedOrganizationData) ?? []
                      ).filter((o) => o.key)}
                    />
                  </div>
                </div>
                <span className={styles.grayLabel}>Or status of:</span>
                <div>
                  <Checkbox
                    disabled={
                      !!state.values.recruitingStatuses?.length || interestedIn
                    }
                    id="attended-or-graduated-from"
                    key={`attended-${attendedOrGraduatedFrom}`}
                    label="Attended / Graduated"
                    onChange={handleAttendedOrGraduatedFrom}
                    value={attendedOrGraduatedFrom}
                  />
                  <div data-show={attendedOrGraduatedFrom}>
                    <OrganizationAutoComplete
                      id={SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={(
                        autoCompleteData(attendedGraduatedData) ?? []
                      ).filter((o) => o.key)}
                    />
                  </div>

                  <Checkbox
                    disabled={
                      attendedOrGraduatedFrom ||
                      !!state.values.recruitingStatuses?.length
                    }
                    id="interested-in"
                    key={`interested-${interestedIn}`}
                    label="Interested In"
                    onChange={handleInterestedIn}
                    value={interestedIn}
                  />
                  <div data-show={interestedIn}>
                    <OrganizationAutoComplete
                      id={SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={(
                        autoCompleteData(recruitmentInterestData) ?? []
                      ).filter((o) => o.key)}
                    />
                  </div>
                </div>
              </div>

              <PersonAutoComplete
                id={SearchKeys.RECRUITED_BY_PERSON_KEY}
                label="Recruiting Coach"
                limit={1}
                name={SearchKeys.RECRUITED_BY_PERSON_KEY}
                onChange={handleAutoCompleteChange}
                optionTemplate={(v) => ({
                  key: v.key,
                  value: v.key,
                  label: `${v.name}${
                    v.currentOrganization?.fullName
                      ? ` (${v.currentOrganization.fullName})`
                      : ''
                  }`,
                })}
                value={(autoCompleteData(recruitedByPersonData) ?? []).filter(
                  (o) => o.key,
                )}
              />
              <Checkbox
                id={SearchKeys.IS_DRAFT_PICK}
                key={`${SearchKeys.IS_DRAFT_PICK}-${state.values.isDraftPick}`}
                label="Drafted"
                onChange={(value) =>
                  dispatch({
                    type: 'update',
                    field: SearchKeys.IS_DRAFT_PICK,
                    value: String(value),
                  })
                }
                value={state.values.isDraftPick ?? false}
              />
            </div>
            <div className={styles.container} data-name="visits">
              <h3>Visits</h3>
              <div className={styles.groupingSmall}>
                <span className={styles.label}>Official Visits</span>
                <span className={styles.grayLabel}>
                  Only include players with scheduled official visits at:
                </span>
                <OrganizationAutoComplete
                  id={SearchKeys.OFFICIAL_VISIT_ORG_KEYS}
                  label="College Team"
                  name={SearchKeys.OFFICIAL_VISIT_ORG_KEYS}
                  onChange={handleAutoCompleteChange}
                  type={OrganizationType.College}
                  value={(autoCompleteData(officialVisitData) ?? []).filter(
                    (o) => o.key,
                  )}
                />

                <Input
                  label="Start Date"
                  name={SearchKeys.OFFICIAL_VISIT_START_DATE}
                  onChange={handleFieldUpdate}
                  type="date"
                  value={state.values.officialVisitStartDate}
                />
                <Input
                  label="End Date"
                  name={SearchKeys.OFFICIAL_VISIT_END_DATE}
                  onChange={handleFieldUpdate}
                  type="date"
                  value={state.values.officialVisitEndDate}
                />
              </div>
            </div>
            <div className={styles.container} data-name="nil">
              <h3>Nil</h3>
              <div className={styles.groupingSmall}>
                <span className={styles.label}>Nil Valuation</span>
                <Checkbox
                  id="publishedNilValuation"
                  key={state.values.minNilValuation}
                  label="Only show players with a published NIL Valuation ($200k+)"
                  onChange={(value) =>
                    dispatch({
                      value: value ? '200000' : '',
                      type: 'update',
                      field: SearchKeys.MIN_NIL_VALUATION,
                    })
                  }
                  value={Boolean(state.values.minNilValuation)}
                />
              </div>
            </div>
          </div>
          <div className={styles.buttons}>
            <button
              aria-label="Advanced Search"
              className={styles.btnCollapse}
              data-status={collapsed ? 'closed' : 'open'}
              name="collapse"
              onClick={() => setCollapsed(!collapsed)}
              type="button"
            >
              Adv. Search
              <On3IconArrowDown />
            </button>
            <button
              className={styles.btnSearch}
              disabled={state.searching}
              name="search"
              onClick={handleSubmit}
              type="button"
            >
              Search
            </button>
            <button
              className={styles.btnReset}
              name="reset"
              onClick={handleResets}
              type="button"
            >
              Reset all
            </button>
          </div>
        </div>
      </aside>
      {searchData.list?.length ? (
        <section className={styles.list}>
          <div className={styles.matches}>{pagination?.count} matches</div>
          <div className={styles.columnHeaders}>
            <span>Player</span>
            <span>Pos</span>
            <span>Rating</span>
            <span>Status</span>
            <span>Athlete-Verified</span>
          </div>

          <ol>
            {searchData.list.map((person) => (
              <AdvancedSearchItem key={person.key} person={person} />
            ))}
          </ol>

          <Pagination data={pagination} type="expanded" />
        </section>
      ) : state.hasSearched ? (
        <div className={styles.noResults}>
          No results found. Try a different search or adjust filters.
          {!!state.filterCount && (
            <button onClick={handleResets}>Clear Filters</button>
          )}
        </div>
      ) : null}
    </section>
  );
};
